// Shared mortgage math + default assumptions. This is the single source of
// truth so the /calculator Mortgage tab and the property browser's "Shop by
// monthly payment" affordability mode stay in sync (no duplicated constants).

export interface MortgageAssumptions {
  downPercent: number; // % down payment
  rate: number; // annual interest rate, e.g. 6.5
  termYears: number; // loan term in years
  propertyTaxPercent: number; // annual property tax as % of price
  insuranceAnnual: number; // annual homeowners insurance ($)
}

// Defaults mirror the /calculator Mortgage tab's initial inputs.
export const MORTGAGE_DEFAULTS: MortgageAssumptions = {
  downPercent: 20,
  rate: 6.5,
  termYears: 30,
  propertyTaxPercent: 1.2,
  insuranceAnnual: 1200,
};

// The monthly principal-and-interest factor per dollar financed.
// PI = principal * factor. Handles a 0% rate gracefully.
function piFactor(rate: number, termYears: number): number {
  const monthlyRate = rate / 100 / 12;
  const numPayments = termYears * 12;
  if (numPayments <= 0) return 0;
  if (monthlyRate === 0) return 1 / numPayments;
  const growth = Math.pow(1 + monthlyRate, numPayments);
  return (monthlyRate * growth) / (growth - 1);
}

export interface MonthlyBreakdown {
  principal: number; // financed amount
  principalInterest: number;
  propertyTax: number;
  insurance: number;
  total: number;
}

// Full monthly payment breakdown for a given price + assumptions. Mirrors the
// /calculator Mortgage tab's math exactly.
export function monthlyPayment(
  price: number,
  a: MortgageAssumptions = MORTGAGE_DEFAULTS
): MonthlyBreakdown {
  const principal = price * (1 - a.downPercent / 100);
  const principalInterest = principal * piFactor(a.rate, a.termYears);
  const propertyTax = (price * (a.propertyTaxPercent / 100)) / 12;
  const insurance = a.insuranceAnnual / 12;
  const total = principalInterest + propertyTax + insurance;
  return {
    principal,
    principalInterest: Number.isFinite(principalInterest) ? principalInterest : 0,
    propertyTax,
    insurance,
    total: Number.isFinite(total) ? total : 0,
  };
}

// Invert monthlyPayment: the max purchase price whose all-in monthly payment
// (P&I + property tax + insurance) fits within `budget`. Returns 0 if the
// budget can't even cover fixed insurance.
export function maxPriceForBudget(
  budget: number,
  a: MortgageAssumptions = MORTGAGE_DEFAULTS
): number {
  const factor = piFactor(a.rate, a.termYears);
  // total = price * [ (1 - down%) * factor + taxRate/12 ] + insurance/12
  const perDollar =
    (1 - a.downPercent / 100) * factor + a.propertyTaxPercent / 100 / 12;
  const insuranceMonthly = a.insuranceAnnual / 12;
  if (perDollar <= 0) return 0;
  const price = (budget - insuranceMonthly) / perDollar;
  return price > 0 && Number.isFinite(price) ? price : 0;
}
